/**
 * IntradayPanel.jsx — dashboard/IntradayPanel.jsx
 *
 * A closeable draggable panel that renders IntradayChart for one symbol.
 * Spawned dynamically from the watchlist — multiple can exist simultaneously.
 * Close button (×) removes this panel from App state.
 */

import { useDraggablePanel } from '../../hooks/useDraggablePanel';
import IntradayChart from './IntradayChart';
import { PanelHelp } from '../shared/IndicatorTooltip';

export default function IntradayPanel({ id, symbol, quote, initialPos, initialSize, onClose }) {
  const { panelRef, headerRef, resizeRef, pos, size, isDragging, isResizing } =
    useDraggablePanel(initialPos, initialSize);

  return (
    <div
      ref={panelRef}
      className={`panel${isDragging ? ' dragging' : ''}${isResizing ? ' resizing' : ''}`}
      style={{ left: pos.x, top: pos.y, width: size.w, height: size.h, zIndex: 10 }}
    >
      {/* Header / drag handle */}
      <div ref={headerRef} className="panel-header">
        <div className="panel-title">
          <span className="icon">⌘</span>
          <PanelHelp
            title={`1-Min · ${symbol} · BB(20,1σ) · VWAP Trend · RSI(14)`}
            help="1-minute bars refreshing every 30s. Bollinger Bands SMA20 ±1σ, VWAP (session reset at open), RSI(14) overbought/oversold zones."
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span className="faint mono" style={{ fontSize: 9 }}>
            {Math.round(size.w)}×{Math.round(size.h)}
          </span>
          {/* Close button */}
          <button
            onPointerDown={e => e.stopPropagation()} // don't trigger drag
            onClick={() => onClose(id)}
            title={`Close ${symbol} chart`}
            style={{
              background: 'none',
              border: '1px solid var(--border)',
              color: 'var(--faint)',
              borderRadius: 4,
              width: 20, height: 20,
              cursor: 'pointer',
              fontSize: 14,
              lineHeight: '18px',
              textAlign: 'center',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'color .15s, background .15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = 'var(--down)'; e.currentTarget.style.background = 'var(--down-soft)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'var(--faint)'; e.currentTarget.style.background = 'none'; }}
          >
            ×
          </button>
        </div>
      </div>

      {/* Chart content */}
      <div className="panel-body">
        <IntradayChart symbol={symbol} quote={quote} />
      </div>

      {/* Resize grip */}
      <div ref={resizeRef} className="resize-handle" title="Drag to resize" />
    </div>
  );
}
